import { useQueryClient } from '@tanstack/react-query'
import {
  getGetAssistanceEmailManagerHistoryQueryKey,
  useCreateAssistanceEmailManager,
  useGetAssistanceEmailManagerHistory,
} from '@transreport/pauk-rrms-api'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AppButton,
  Modal,
  SearchableSelect,
  type SearchableSelectOption,
  SkeletonBlock,
} from '@/components/framework'
import { trackOutcome } from '@/lib/analytics'

interface EmailManagerModalProps {
  isOpen: boolean
  onClose: () => void
  assistanceId: string
  area: 'timeline' | 'requests'
}

const splitRecipients = (raw: string): string[] =>
  raw
    .split(/[;,\n]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)

const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const EMAIL_REASON_OPTIONS = [
  'Missed Assistance',
  'Late Assistance',
  'Un-assigned Assistance',
  'Declined/ Refused to Assist',
  'Other',
] as const

const EMAIL_REASON_SELECT_OPTIONS: SearchableSelectOption[] = EMAIL_REASON_OPTIONS.map(
  (reason) => ({ id: reason, value: reason, label: reason })
)

const formatEmailDate = (value: string): string =>
  new Date(value).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

const EmailHistorySkeleton = ({ label }: { label: string }) => (
  <div role="status" aria-label={label} className="space-y-3">
    <span className="sr-only">{label}</span>
    {[0, 1].map((item) => (
      <div key={item} aria-hidden="true" className="rounded border border-gray-200 bg-gray-50 p-3">
        <SkeletonBlock className="h-4 w-3/4" />
        <SkeletonBlock className="mt-2 h-4 w-2/3" />
        <SkeletonBlock className="mt-2 h-4 w-1/2" />
        <SkeletonBlock className="mt-3 h-12 w-full" />
      </div>
    ))}
  </div>
)

export const EmailManagerModal: React.FC<EmailManagerModalProps> = ({
  isOpen,
  onClose,
  assistanceId,
  area,
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [recipients, setRecipients] = useState<string[]>([])
  const [recipientDraft, setRecipientDraft] = useState('')
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1)
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [message, setMessage] = useState('')
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const errorSummaryRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    setRecipients([])
    setRecipientDraft('')
    setHighlightedSuggestionIndex(-1)
    setSelectedReason('')
    setCustomReason('')
    setMessage('')
    setHasAttemptedSubmit(false)
  }, [isOpen])

  const emailHistoryQuery = useGetAssistanceEmailManagerHistory(assistanceId, {
    query: {
      enabled: isOpen && Boolean(assistanceId),
      staleTime: 60_000,
    },
  })

  const createEmailManagerMutation = useCreateAssistanceEmailManager({
    mutation: {
      meta: {
        toast: {
          success: t('emailManager.saveSuccess'),
          error: t('emailManager.saveError'),
        },
      },
    },
  })
  const isSaving = createEmailManagerMutation.isPending

  const invalidRecipients = useMemo(
    () => recipients.filter((recipient) => !isValidEmail(recipient)),
    [recipients]
  )

  const recipientSuggestions = useMemo(() => {
    const seen = new Set<string>()
    const suggestions: string[] = []
    const history = [...(emailHistoryQuery.data ?? [])].toReversed()

    for (const email of history) {
      for (const recipient of email.recipients) {
        const normalized = recipient.trim().toLowerCase()
        if (!normalized || seen.has(normalized)) continue
        seen.add(normalized)
        suggestions.push(recipient.trim())
      }
    }

    return suggestions
  }, [emailHistoryQuery.data])

  const filteredSuggestions = useMemo(() => {
    const query = recipientDraft.trim().toLowerCase()
    if (!query) return []

    const existing = new Set(recipients.map((recipient) => recipient.toLowerCase()))

    return recipientSuggestions
      .filter((candidate) => candidate.toLowerCase().includes(query))
      .filter((candidate) => !existing.has(candidate.toLowerCase()))
      .slice(0, 8)
  }, [recipientDraft, recipients, recipientSuggestions])

  useEffect(() => {
    if (filteredSuggestions.length === 0) {
      setHighlightedSuggestionIndex(-1)
      return
    }

    setHighlightedSuggestionIndex((current) => {
      if (current < 0) return 0
      return Math.min(current, filteredSuggestions.length - 1)
    })
  }, [filteredSuggestions])

  const isOtherReason = selectedReason === 'Other'
  const resolvedReason = isOtherReason ? customReason.trim() : selectedReason.trim()

  const isFormValid =
    recipients.length > 0 &&
    invalidRecipients.length === 0 &&
    Boolean(resolvedReason) &&
    Boolean(message.trim())

  const addRecipients = (entries: string[]) => {
    if (entries.length === 0) return

    setRecipients((previous) => {
      const merged = [...previous]
      const seen = new Set(previous.map((recipient) => recipient.toLowerCase()))

      for (const entry of entries) {
        const normalized = entry.trim()
        const dedupeKey = normalized.toLowerCase()
        if (!normalized || seen.has(dedupeKey)) continue
        seen.add(dedupeKey)
        merged.push(normalized)
      }

      return merged
    })
  }

  const commitDraftRecipient = () => {
    const parsed = splitRecipients(recipientDraft)
    addRecipients(parsed)
    setRecipientDraft('')
    setHighlightedSuggestionIndex(-1)
  }

  const selectSuggestion = (suggestion: string) => {
    addRecipients([suggestion])
    setRecipientDraft('')
    setHighlightedSuggestionIndex(-1)
  }

  const removeRecipient = (recipientToRemove: string) => {
    setRecipients((previous) => previous.filter((recipient) => recipient !== recipientToRemove))
  }

  const handleClose = () => {
    if (isSaving) return
    setRecipients([])
    setRecipientDraft('')
    setHighlightedSuggestionIndex(-1)
    setSelectedReason('')
    setCustomReason('')
    setMessage('')
    setHasAttemptedSubmit(false)
    onClose()
  }

  const handleSend = async () => {
    if (isSaving) return
    setHasAttemptedSubmit(true)
    const stationContext = trackOutcome('email_sent', area, 'attempted')

    if (!assistanceId || !isFormValid) {
      trackOutcome('email_sent', area, 'failed', { ...stationContext, failure_type: 'validation' })
      window.requestAnimationFrame(() => errorSummaryRef.current?.focus())
      return
    }

    try {
      await createEmailManagerMutation.mutateAsync({
        id: assistanceId,
        data: {
          to: recipients,
          email_reason: resolvedReason,
          message: message.trim(),
        },
      })

      await queryClient.invalidateQueries({
        queryKey: getGetAssistanceEmailManagerHistoryQueryKey(assistanceId),
      })

      trackOutcome('email_sent', area, 'succeeded', stationContext)
      handleClose()
    } catch {
      trackOutcome('email_sent', area, 'failed', { ...stationContext, failure_type: 'api' })
      // The global mutation handler displays the error.
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('emailManager.title')} size="xl">
      <div className="space-y-4">
        {hasAttemptedSubmit && !isFormValid && (
          <div
            ref={errorSummaryRef}
            role="alert"
            tabIndex={-1}
            className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            <p className="font-semibold">{t('emailManager.validation.summary')}</p>
            <ul className="mt-1 list-disc pl-5">
              {recipients.length === 0 && <li>{t('emailManager.validation.toRequired')}</li>}
              {invalidRecipients.length > 0 && <li>{t('emailManager.validation.toInvalid')}</li>}
              {!resolvedReason && <li>{t('emailManager.validation.reasonRequired')}</li>}
              {!message.trim() && <li>{t('emailManager.validation.messageRequired')}</li>}
            </ul>
          </div>
        )}
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <section className="rounded bg-white p-4">
            <div className="space-y-4 text-base text-gray-900">
              <div>
                <label
                  htmlFor="email-manager-to"
                  className="mb-2 block text-sm font-semibold tracking-wide text-gray-500 uppercase"
                >
                  {t('emailManager.toLabel')}
                </label>
                <div className="rounded-input border border-border-gray px-3 py-2 focus-within:outline-3 focus-within:outline-offset-2 focus-within:outline-primary">
                  <div className="flex max-h-28 min-h-11 flex-wrap items-center gap-2 overflow-y-auto">
                    {recipients.map((recipient) => {
                      const isInvalid = !isValidEmail(recipient)

                      return (
                        <span
                          key={recipient}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm ${
                            isInvalid
                              ? 'border border-red-300 bg-red-50 text-red-700'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {recipient}
                          <button
                            type="button"
                            className="cursor-pointer rounded-full px-1 text-xs leading-none hover:bg-black/10"
                            onClick={() => removeRecipient(recipient)}
                            aria-label={`${t('emailManager.removeRecipient')} ${recipient}`}
                          >
                            ×
                          </button>
                        </span>
                      )
                    })}
                    <input
                      id="email-manager-to"
                      value={recipientDraft}
                      onChange={(event) => setRecipientDraft(event.target.value)}
                      onBlur={commitDraftRecipient}
                      onPaste={(event) => {
                        const pastedText = event.clipboardData.getData('text')
                        const parsed = splitRecipients(pastedText)
                        if (parsed.length === 0) return
                        event.preventDefault()
                        addRecipients(parsed)
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'ArrowDown' && filteredSuggestions.length > 0) {
                          event.preventDefault()
                          setHighlightedSuggestionIndex((current) =>
                            current >= filteredSuggestions.length - 1 ? 0 : current + 1
                          )
                          return
                        }

                        if (event.key === 'ArrowUp' && filteredSuggestions.length > 0) {
                          event.preventDefault()
                          setHighlightedSuggestionIndex((current) =>
                            current <= 0 ? filteredSuggestions.length - 1 : current - 1
                          )
                          return
                        }

                        if (
                          event.key === 'Enter' &&
                          filteredSuggestions.length > 0 &&
                          highlightedSuggestionIndex >= 0
                        ) {
                          event.preventDefault()
                          selectSuggestion(filteredSuggestions[highlightedSuggestionIndex])
                          return
                        }

                        const isCommitKey =
                          event.key === 'Enter' ||
                          event.key === 'Tab' ||
                          event.key === ',' ||
                          event.key === ';'

                        if (isCommitKey) {
                          event.preventDefault()
                          commitDraftRecipient()
                          return
                        }

                        if (
                          event.key === 'Backspace' &&
                          recipientDraft.length === 0 &&
                          recipients.length > 0
                        ) {
                          event.preventDefault()
                          setRecipients((previous) => previous.slice(0, -1))
                        }
                      }}
                      className="min-w-0 flex-1 border-0 bg-transparent py-1 text-base focus:outline-none"
                      placeholder={t('emailManager.toPlaceholder')}
                      role="combobox"
                      aria-autocomplete="list"
                      aria-expanded={filteredSuggestions.length > 0}
                      aria-controls="email-manager-to-suggestions"
                      aria-activedescendant={
                        highlightedSuggestionIndex >= 0
                          ? `email-manager-suggestion-${highlightedSuggestionIndex}`
                          : undefined
                      }
                      aria-invalid={
                        hasAttemptedSubmit &&
                        (recipients.length === 0 || invalidRecipients.length > 0)
                      }
                      aria-describedby={`email-manager-to-hint${
                        hasAttemptedSubmit &&
                        (recipients.length === 0 || invalidRecipients.length > 0)
                          ? ' email-manager-to-error'
                          : ''
                      }`}
                    />
                  </div>
                </div>
                <p id="email-manager-to-hint" className="mt-2 text-xs text-text-gray-500">
                  {t('emailManager.toHint')}
                </p>
                {filteredSuggestions.length > 0 && (
                  <div
                    id="email-manager-to-suggestions"
                    className="mt-2 space-y-1 rounded border border-gray-200 bg-white p-2"
                  >
                    <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                      {t('emailManager.suggestions')}
                    </p>
                    <div className="flex flex-wrap gap-1" role="listbox">
                      {filteredSuggestions.map((suggestion, index) => (
                        <button
                          key={suggestion}
                          id={`email-manager-suggestion-${index}`}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault()
                            selectSuggestion(suggestion)
                          }}
                          className={`cursor-pointer rounded-full border px-2.5 py-1 text-sm ${
                            index === highlightedSuggestionIndex
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                          role="option"
                          aria-selected={index === highlightedSuggestionIndex}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {hasAttemptedSubmit && recipients.length === 0 && (
                  <p id="email-manager-to-error" className="mt-2 text-sm text-danger">
                    {t('emailManager.validation.toRequired')}
                  </p>
                )}
                {hasAttemptedSubmit && invalidRecipients.length > 0 && (
                  <p id="email-manager-to-error" className="mt-2 text-sm text-danger">
                    {t('emailManager.validation.toInvalid')}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email-manager-reason-select"
                  className="mb-2 block text-sm font-semibold tracking-wide text-gray-500 uppercase"
                >
                  {t('emailManager.reasonLabel')}
                </label>
                <SearchableSelect
                  id="email-manager-reason-select"
                  selectedValue={selectedReason}
                  options={EMAIL_REASON_SELECT_OPTIONS}
                  onChange={(option) => setSelectedReason(option.value)}
                  placeholder={t('emailManager.reasonSelectPlaceholder')}
                  onClear={() => setSelectedReason('')}
                  clearButtonAriaLabel={t('emailManager.clearReason')}
                  ariaInvalid={hasAttemptedSubmit && !resolvedReason}
                  ariaDescribedBy={
                    hasAttemptedSubmit && !resolvedReason ? 'email-manager-reason-error' : undefined
                  }
                />
              </div>

              {isOtherReason && (
                <div>
                  <label
                    htmlFor="email-manager-reason-input"
                    className="mb-2 block text-sm font-semibold tracking-wide text-gray-500 uppercase"
                  >
                    {t('emailManager.reasonInputLabel')}
                  </label>
                  <input
                    id="email-manager-reason-input"
                    type="text"
                    value={customReason}
                    aria-invalid={hasAttemptedSubmit && !resolvedReason}
                    aria-describedby={
                      hasAttemptedSubmit && !resolvedReason
                        ? 'email-manager-reason-error'
                        : undefined
                    }
                    onChange={(event) => setCustomReason(event.target.value)}
                    className="w-full rounded-input border border-border-gray px-4 py-2.5 text-base"
                    placeholder={t('emailManager.reasonInputPlaceholder')}
                  />
                </div>
              )}

              {hasAttemptedSubmit && !resolvedReason && (
                <p id="email-manager-reason-error" className="text-sm text-danger">
                  {t('emailManager.validation.reasonRequired')}
                </p>
              )}

              <div>
                <label
                  htmlFor="email-manager-message"
                  className="mb-2 block text-sm font-semibold tracking-wide text-gray-500 uppercase"
                >
                  {t('emailManager.messageLabel')}
                </label>
                <textarea
                  id="email-manager-message"
                  value={message}
                  aria-invalid={hasAttemptedSubmit && message.trim().length === 0}
                  aria-describedby={
                    hasAttemptedSubmit && message.trim().length === 0
                      ? 'email-manager-message-error'
                      : undefined
                  }
                  onChange={(event) => setMessage(event.target.value)}
                  rows={4}
                  className="w-full rounded-input border border-border-gray px-4 py-3 text-base"
                  placeholder={t('emailManager.messagePlaceholder')}
                />
                {hasAttemptedSubmit && message.trim().length === 0 && (
                  <p id="email-manager-message-error" className="mt-2 text-sm text-danger">
                    {t('emailManager.validation.messageRequired')}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded bg-white p-4">
            <h3 className="mb-3 text-sm font-semibold tracking-wide text-gray-500 uppercase">
              {t('emailManager.previousEmails')}
            </h3>

            {emailHistoryQuery.isPending ? (
              <EmailHistorySkeleton label={t('emailManager.loading')} />
            ) : emailHistoryQuery.error ? (
              <p role="alert" className="text-sm text-danger">
                {t('emailManager.loadError')}
              </p>
            ) : (emailHistoryQuery.data ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">{t('emailManager.noHistory')}</p>
            ) : (
              <div className="space-y-3">
                {(emailHistoryQuery.data ?? []).map((email, index) => (
                  <article
                    key={`${email.created_at}-${email.sender_username}-${index}`}
                    className="rounded border border-gray-200 bg-gray-50 p-3"
                  >
                    <p className="text-sm text-gray-800">
                      <span className="font-semibold">{t('emailManager.history.to')}:</span>{' '}
                      {email.recipients.join(', ')}
                    </p>
                    <p className="text-sm text-gray-800">
                      <span className="font-semibold">{t('emailManager.history.meta')}:</span>{' '}
                      {formatEmailDate(email.created_at)} - {email.reason}
                    </p>
                    <p className="text-sm text-gray-800">
                      <span className="font-semibold">{t('emailManager.history.by')}:</span>{' '}
                      {email.sender_username}
                    </p>
                    <p className="mt-1 text-sm break-words whitespace-pre-wrap text-gray-700">
                      "{email.message}"
                    </p>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="sticky bottom-0 z-10 -mx-modal-padding mt-1 flex justify-end gap-gap-sm border-t border-border-lighter bg-white px-modal-padding pt-3 pb-0.5">
          <AppButton variant="outline" size="md" onClick={handleClose} disabled={isSaving}>
            {t('emailManager.cancel')}
          </AppButton>
          <AppButton
            variant="primary"
            size="md"
            onClick={handleSend}
            disabled={isSaving}
            loading={isSaving}
          >
            {t('emailManager.send')}
          </AppButton>
        </div>
      </div>
    </Modal>
  )
}
